import { Configuration } from "@seriously-rad/seriouslyrad-framework-core/Configuration";
import { EntityCommandTypeEnum } from "@seriously-rad/seriouslyrad-framework-core/EntityCommand";
import { WindowShim } from "@seriously-rad/seriouslyrad-framework-core/RadWindow";
import { registerPageManagerConfiguration } from "@seriously-rad/seriouslyrad-framework-core/Registration";
import { DotNetTypeMapper } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/DotNetTypeMapper";
import { Evaluator } from "@seriously-rad/seriouslyrad-framework-core/Server/DotNet/SeriouslyRAD.Framework/Evaluation/Evaluator";
import { ClassEditor } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ClassEditor";
import { PropertyEditorNew } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/PropertyEditorNew";
import { EntityCommandProcessDesign } from "./EntityCommands/EntityCommandProcessDesign";
import { EntityCommandProcessDuplicate } from "./EntityCommands/EntityCommandProcessDuplicate";
import { EntityCommandProcessExport } from "./EntityCommands/EntityCommandProcessExport";
import { EntityCommandProcessImport } from "./EntityCommands/EntityCommandProcessImport";
import { EntityCommandProcessInstanceFilter } from "./EntityCommands/EntityCommandProcessInstanceFilter";
import { EntityCommandProcessInstanceItemOpen } from "./EntityCommands/EntityCommandProcessInstanceItemOpen";
import { EntityCommandProcessInstanceItemRetry } from "./EntityCommands/EntityCommandProcessInstanceItemRetry";
import { EntityCommandProcessInstanceItemReturnToPreviousTask } from "./EntityCommands/EntityCommandProcessInstanceItemReturnToPreviousTask";
import { EntityCommandProcessInstanceItemStartEntityProcess } from "./EntityCommands/EntityCommandProcessInstanceItemStartEntityProcess";
import { EntityCommandProcessInstanceItemTerminateProcessInstance } from "./EntityCommands/EntityCommandProcessInstanceItemTerminateProcessInstance";
import { EntityCommandProcessInstanceItemViewError } from "./EntityCommands/EntityCommandProcessInstanceItemViewError";
import { EntityCommandProcessInstanceItemViewProcessInstance } from "./EntityCommands/EntityCommandProcessInstanceItemViewProcessInstance";
import { EntityCommandProcessInstanceItemViewProcessVersion } from "./EntityCommands/EntityCommandProcessInstanceItemViewProcessVersion";
import { EntityCommandProcessInstanceTerminate } from "./EntityCommands/EntityCommandProcessInstanceTerminate";
import { EntityCommandProcessPublish } from "./EntityCommands/EntityCommandProcessPublish";
import { EntityCommandProcessVersionStartProcessVersion } from "./EntityCommands/EntityCommandProcessVersionStartProcessVersion";
import { ProcessItemServiceManager } from "./ProcessItemServices/ProcessItemServiceManager";
import { ProcessItemSupportsInterruptOptionEvaluator } from "./Server/DotNet/SeriouslyRAD.Framework.Data.Processes/Evaluation/ProcessItemSupportsInterruptOptionEvaluator";
import { RuntimeDataType } from "./Ui/Controls/RuntimeDataType";
import { RuntimeProcessPropertyInput } from "./Ui/Controls/RuntimeProcessPropertyInput";
import { RuntimeSourceCodeProcess } from "./Ui/Controls/RuntimeSourceCodeProcess";
import { RuntimeUsingDirectivesProcess } from "./Ui/Controls/RuntimeUsingDirectivesProcess";
import pageManagerConfiguration from "./Ui/PageManagerConfiguration";

import "./Registration.scss";

const
    radWindow = WindowShim();

export async function registerModuleProcessesAsync(configuration: Configuration)
{
    registerDotNetTypes();

    // Process
    radWindow.entityManager?.registerCommand(new EntityCommandProcessDesign());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessDuplicate());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessExport());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessImport());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessPublish());

    // Process Instance
    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceFilter());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceTerminate());

    // Process Instance Item
    radWindow.entityManager?.removeCommand({
        type: EntityCommandTypeEnum.entity,
        entityName: "ProcessInstanceItem",
        key: "DELETE"
    });
    radWindow.entityManager?.removeCommand({
        type: EntityCommandTypeEnum.entity,
        entityName: "ProcessInstanceItem",
        key: "OPEN"
    });
    radWindow.entityManager?.removeCommand({
        type: EntityCommandTypeEnum.entitySet,
        entityName: "ProcessInstanceItem",
        key: "NEW"
    });

    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceItemOpen());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceItemRetry());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceItemReturnToPreviousTask());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceItemStartEntityProcess());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceItemTerminateProcessInstance());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceItemViewError());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceItemViewProcessInstance());
    radWindow.entityManager?.registerCommand(new EntityCommandProcessInstanceItemViewProcessVersion());

    // Process Version
    radWindow.entityManager?.registerCommand(new EntityCommandProcessVersionStartProcessVersion());

    //$$(document).on("SeriouslyRAD.DetailsPage:childControlsCreated", (evt: IJNativeEvent, detailsPage: DetailsPage) =>
    //{
    //    if (detailsPage.tabPages)
    //    {
    //        const
    //            tabPage =
    //                new TabPage({ text: "Process Activities", visibleEvaluator: new PermissionEvaluator({ entityName: "ProcessInstanceItem", permission: PermissionEvaluator.permissionLevels.read }) })
    //                    .appendTo(detailsPage.tabPages);

    //        new EntityProcessInstanceItemSearch()
    //            .appendTo(tabPage);
    //    }
    //});

    PropertyEditorNew.registerEditorControlOverride(propertyInfo =>
    {
        if (/SeriouslyRAD\.Framework\.Data\.Processes\.Runtime\.RuntimeProcessProperty(?:`[0-9]+\[.*\])?, SeriouslyRAD\.Framework\.Data\.Processes/.test(propertyInfo.propertyTypeId))
            return new RuntimeProcessPropertyInput();

        switch (propertyInfo.propertyTypeId)
        {
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeDataType, SeriouslyRAD.Framework.Data.Processes":
                return new RuntimeDataType();
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeProcessProperty, SeriouslyRAD.Framework.Data.Processes":
                return new RuntimeProcessPropertyInput();
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeSourceCodeProcess, SeriouslyRAD.Framework.Data.Processes":
                return new RuntimeSourceCodeProcess();
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeUsingDirectivesProcess, SeriouslyRAD.Framework.Data.Processes":
                return new RuntimeUsingDirectivesProcess();
        }

        return undefined;
    });

    ClassEditor.registerOverride(async (propertyInfo) =>
    {
        if (/SeriouslyRAD\.Framework\.Data\.Processes\.Runtime\.RuntimeConstantOrProcessProperty(?:`[0-9]+\[.*\])?, SeriouslyRAD\.Framework\.Data\.Processes/.test(propertyInfo.propertyTypeId))
            return new (await import("./Ui/Controls/RuntimeConstantOrProcessPropertyInput")).RuntimeConstantOrProcessPropertyInput();

        if (/SeriouslyRAD\.Framework\.Data\.Processes\.Runtime\.RuntimeConstantProperty(?:`[0-9]+\[.*\])?, SeriouslyRAD\.Framework\.Data\.Processes/.test(propertyInfo.propertyTypeId))
            return new (await import("./Ui/Controls/RuntimeConstantPropertyInput")).RuntimeConstantPropertyInput();

        if (/SeriouslyRAD\.Framework\.Data\.Processes\.Runtime\.RuntimeProcessProperty(?:`[0-9]+\[.*\])?, SeriouslyRAD\.Framework\.Data\.Processes/.test(propertyInfo.propertyTypeId))
            return new (await import("./Ui/Controls/RuntimeProcessPropertyInput")).RuntimeProcessPropertyInput();

        switch (propertyInfo.propertyTypeId)
        {
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeDataType, SeriouslyRAD.Framework.Data.Processes":
                return new (await import("./Ui/Controls/RuntimeDataType")).RuntimeDataType();
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeProcessProperty, SeriouslyRAD.Framework.Data.Processes":
                return new (await import("./Ui/Controls/RuntimeProcessPropertyInput")).RuntimeProcessPropertyInput();

            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeSourceCodeProcess, SeriouslyRAD.Framework.Data.Processes":
                return new (await import("./Ui/Controls/RuntimeSourceCodeProcess")).RuntimeSourceCodeProcess();
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeSourceCodeProcessItem, SeriouslyRAD.Framework.Data.Processes":
                return new (await import("./Ui/Controls/RuntimeSourceCodeProcessItem")).RuntimeSourceCodeProcessItem();
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeSourceCodeProcessPath, SeriouslyRAD.Framework.Data.Processes":
                return new (await import("./Ui/Controls/RuntimeSourceCodeProcessPath")).RuntimeSourceCodeProcessPath();

            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeUsingDirectivesProcess, SeriouslyRAD.Framework.Data.Processes":
                return new (await import("./Ui/Controls/RuntimeUsingDirectivesProcess")).RuntimeUsingDirectivesProcess();
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeUsingDirectivesProcessItem, SeriouslyRAD.Framework.Data.Processes":
                return new (await import("./Ui/Controls/RuntimeUsingDirectivesProcessItem")).RuntimeUsingDirectivesProcessItem();
            case "SeriouslyRAD.Framework.Data.Processes.Runtime.RuntimeUsingDirectivesProcessPath, SeriouslyRAD.Framework.Data.Processes":
                return new (await import("./Ui/Controls/RuntimeUsingDirectivesProcessPath")).RuntimeUsingDirectivesProcessPath();
        }
    });

    registerPageManagerConfiguration(configuration, pageManagerConfiguration);

    ProcessItemServiceManager.add("10DCD068-D8CD-4D1E-8A18-B0ADDF7A8D10", async () => new (await import("./ProcessItemServices/UserTaskProcessItemService")).UserTaskProcessItemService());
    ProcessItemServiceManager.add("74091C08-48DA-4DDA-BB02-C4FACCE3E548", async () => new (await import("./ProcessItemServices/FormUserTaskProcessItemService")).FormUserTaskProcessItemService());
    ProcessItemServiceManager.add("EC8DDBFA-DE8B-401C-8D06-29CC5DFC71B3", async () => new (await import("./ProcessItemServices/YesNoUserTaskProcessItemService")).YesNoUserTaskProcessItemService());
}

function registerDotNetTypes()
{
    DotNetTypeMapper.addTypes(
        [
            { typeIdRegex: "SeriouslyRAD.Framework.Data.Processes.Evaluation.ProcessItemSupportsInterruptOptionEvaluator, SeriouslyRAD.Framework.Data.Processes", createFn: async () => (await import("./Server/DotNet/SeriouslyRAD.Framework.Data.Processes/Evaluation/ProcessItemSupportsInterruptOptionEvaluator")).ProcessItemSupportsInterruptOptionEvaluator },
        ]
    );
}